import { useEffect, useState } from 'react';
import { Modal, Radio, Typography, Spin } from 'antd';
import type { RadioChangeEvent } from 'antd';
import './index.scss';
import CustomButton from '../../../../../components/buttons/CustomButton';
import { ShippingAddress } from '../../../../../apis/client-axios';

const { Title, Text } = Typography;

interface Address {
  id: string;
  type: 'original' | 'suggested';
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  highlight?: string;
  stateOrProvince: string;
  countryCode: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onConfirm: (selectedAddress: Address) => void;
  dataOrigin: Omit<ShippingAddress, 'AddressID' | 'AddressOwner' | 'CountryName' | 'Phone'>;
  dataSuggested: Omit<ShippingAddress, 'AddressID' | 'AddressOwner' | 'CountryName' | 'Phone'>;
}

const AddressSuggestionModal = ({ visible, onClose, onConfirm, dataOrigin, dataSuggested }: Props) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const mapAddress = (ShippingAddress: typeof dataOrigin) => {
    const addressLine1 = ShippingAddress?.Street1 || '';
    const addressLine2 = ShippingAddress?.Street2 || '';
    const city = ShippingAddress?.CityName || '';
    const stateOrProvince = ShippingAddress?.StateOrProvince || '';
    const countryCode = ShippingAddress.Country || '';

    return `${addressLine1}, ${addressLine2 ? `${addressLine2},` : ''} ${city}, ${stateOrProvince ? `${stateOrProvince},` : ''} ${countryCode}`;
  };

  const fetchAddresses = async () => {
    setLoading(true);
    const data: Address[] = [
      {
        id: '1',
        type: 'original',
        addressLine1: dataOrigin.Street1,
        addressLine2: dataOrigin?.Street2,
        city: dataOrigin.CityName,
        stateOrProvince: dataOrigin.StateOrProvince,
        postalCode: dataOrigin.PostalCode,
        countryCode: dataOrigin.Country,
      },
      {
        id: '2',
        type: 'suggested',
        addressLine1: dataSuggested.Street1,
        addressLine2: dataSuggested.Street2,
        city: dataSuggested.CityName,
        stateOrProvince: dataSuggested.StateOrProvince,
        postalCode: dataSuggested.PostalCode,
        countryCode: dataSuggested.Country,
      },
    ];
    setTimeout(() => {
      setAddresses(data);
      setSelectedId('2');
      setLoading(false);
    }, 200);
  };

  const renderSuggestedField = (key: keyof Address, origin: Address, suggested: Address) => {
    const isDiff = (origin[key] || '').trim() !== (suggested[key] || '').trim();

    return <span className={isDiff ? 'highlight' : ''}>{suggested[key]}</span>;
  };

  const handleChange = (e: RadioChangeEvent) => {
    setSelectedId(e.target.value);
  };

  const handleConfirm = () => {
    const selected = addresses.find((addr) => addr.id === selectedId);
    if (selected) onConfirm(selected);
  };

  useEffect(() => {
    if (visible) fetchAddresses();
  }, [visible]);

  return (
    <Modal open={visible} onCancel={onClose} footer={null} centered width={630} closable className="address-modal">
      <div className="address-modal-content">
        <Title level={4}>バイヤー住所変更</Title>
        <Text type="secondary">
          バイヤーの住所が不明確なようです。配送トラブルを防止するため、提案された住所への変更をお勧めします。
        </Text>

        {loading ? (
          <div className="loading-spinner">
            <Spin />
          </div>
        ) : (
          <Radio.Group onChange={handleChange} value={selectedId} className="address-options">
            {addresses.map((addr) => (
              <Radio key={addr.id} value={addr.id} className={`address-box ${addr.type}`}>
                <div className="address-title">{addr.type === 'original' ? 'ebay上の住所' : '提案された住所'}</div>
                <div className="address-content">
                  {addr.type === 'original' ? (
                    mapAddress(dataOrigin)
                  ) : (
                    <>
                      {renderSuggestedField('addressLine1', addresses[0], addr)}
                      {', '}
                      {addr.addressLine2 ? <>{renderSuggestedField('addressLine2', addresses[0], addr)}, </> : null}
                      {renderSuggestedField('city', addresses[0], addr)}
                      {', '}
                      {renderSuggestedField('stateOrProvince', addresses[0], addr)}
                      {', '}
                      {renderSuggestedField('countryCode', addresses[0], addr)}
                    </>
                  )}
                </div>
                <div className="address-content">
                  {addr.type === 'original'
                    ? dataOrigin.PostalCode
                    : renderSuggestedField('postalCode', addresses[0], addr)}
                </div>
              </Radio>
            ))}
          </Radio.Group>
        )}

        <div className="modal-footer">
          <CustomButton size="large" disabled={!selectedId} onClick={handleConfirm} className="btn-submit">
            進める
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default AddressSuggestionModal;
